import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import {
  PROBLEMS,
  WHY_NOW,
  LOOP_STAGES,
  LOOP_PROPERTIES,
  PRINCIPLES,
  GUARANTEES,
  STATE_EQUATION,
  DELTA,
  COMPARISON,
  ARCHITECTURE_LAYERS,
  SCALE_FACTS,
} from '../data/ewm';
import './Narrative.css';

function Technology() {
  return (
    <div className="np-page">
      <SEO {...getSEOData('technology')} />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">Technology</span>
          <h1>Do not touch the model — put a measuring side-car next to it</h1>
          <p className="np-hero-sub">
            A structural memory and working-context layer for any AI model. The host keeps its
            weights, tokenizer, prompts and APIs; the side-car consumes the model&rsquo;s own
            encodings and hands encodings back. Nothing upstream changes.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/evidence" className="np-btn np-btn--ghost">See the measured results</Link>
            <Link to="/partners" className="np-btn np-btn--ghost">Become a design partner</Link>
          </div>
        </div>
      </section>

      {/* The problem */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">AI has no structural memory — only a window and a summary</h2>
          <p className="np-lead">
            Every model shipped today is memoryless between calls. What passes for memory is a fixed
            context window, re-sent each turn, or a lossy summary.
          </p>
          <div className="np-grid np-grid--3">
            {PROBLEMS.map((p) => (
              <div key={p.id} className="np-card">
                <span className="np-card-num">{p.id}</span>
                <h3 className="np-h3">{p.title}</h3>
                <p className="np-body">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="np-caveat">
            <h3 className="np-h3">In one sentence</h3>
            <p className="np-body">
              The bottleneck has moved from reasoning to memory — and there is still no algebra
              for memory.
            </p>
          </div>
        </div>
      </section>

      {/* Why now */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Four shifts turned memory into the binding constraint</h2>
          <p className="np-lead">
            The market is already bolting memory onto models — pragmatically, per vendor, and
            without formal guarantees.
          </p>
          <div className="np-grid np-grid--4">
            {WHY_NOW.map((w) => (
              <div key={w.id} className="np-card">
                <h3 className="np-h3">{w.title}</h3>
                <p className="np-body">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The loop */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">Encodings in, encodings out — the whole loop</h2>
          <p className="np-lead">
            The loop is deliberately small, and identical for a language model, a vision encoder or
            a driving stack.
          </p>

          <div className="np-flow">
            {LOOP_STAGES.map((s) => (
              <div key={s.id} className="np-step">
                <div className="np-step-label">Step {s.id}</div>
                <div className="np-step-title">{s.step}</div>
                <p className="np-step-detail">{s.detail}</p>
              </div>
            ))}
          </div>

          <div className="np-grid np-grid--3">
            {LOOP_PROPERTIES.map((p) => (
              <div key={p.title} className="np-card np-card--accent">
                <h3 className="np-h3">{p.title}</h3>
                <p className="np-body">{p.body}</p>
              </div>
            ))}
          </div>

          <p className="np-source" style={{ marginTop: '22px' }}>
            State is persisted outside the processing unit: cache + content-addressed commit DAG,
            where the tip is the head of the stack. The unit itself owns nothing but a handle —
            which is why a crash costs a pop, not a rebuild.
          </p>
        </div>
      </section>

      {/* The state equation */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Everything the side-car needs is five sets</h2>
          <p className="np-lead">
            The state machine is algebraic, not neural. Every term is a bit-set, and every
            operation is a bit operation.
          </p>

          <div className="np-formula">{STATE_EQUATION.formula}</div>

          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>Term</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                {STATE_EQUATION.terms.map((t) => (
                  <tr key={t.symbol}>
                    <td><strong>{t.symbol}</strong></td>
                    <td>{t.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="np-grid np-grid--4" style={{ marginTop: '26px' }}>
            {STATE_EQUATION.identities.map((i) => (
              <div key={i} className="np-card" style={{ alignItems: 'center' }}>
                <span className="np-formula" style={{ margin: 0, fontSize: '0.92rem' }}>{i}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">Four principles, one algebra</h2>
          <p className="np-lead">
            Each principle is stated as a structural requirement — then realized in the HLLSet
            implementation.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '200px' }}>Principle</th>
                  <th>Statement</th>
                  <th>What it buys</th>
                </tr>
              </thead>
              <tbody>
                {PRINCIPLES.map((p) => (
                  <tr key={p.id}>
                    <td><strong>{p.name}</strong></td>
                    <td>{p.statement}</td>
                    <td>{p.buys}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Five guarantees the side-car inherits from the algebra</h2>
          <p className="np-lead">
            These are properties of the construction, not implementation promises — they hold for
            any host model, at any scale.
          </p>
          <div className="np-grid np-grid--3">
            {GUARANTEES.map((g) => (
              <div key={g.name} className="np-card np-card--highlight">
                <h3 className="np-h3">{g.name}</h3>
                <p className="np-body">{g.body}</p>
              </div>
            ))}
          </div>
          <p className="np-source" style={{ marginTop: '22px' }}>
            Fail-safe by construction: an uncommitted unit can be thrown away and rebuilt from the
            tip, because re-processing is idempotent.
          </p>
        </div>
      </section>

      {/* Delta */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">What changes when a model gets a structural side-car</h2>
          <p className="np-lead">
            Same model, same prompts, same deployment — different memory semantics.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '170px' }}>Dimension</th>
                  <th>Today, with a context window</th>
                  <th>With an EWM side-car</th>
                </tr>
              </thead>
              <tbody>
                {DELTA.map((d) => (
                  <tr key={d.dimension}>
                    <td><strong>{d.dimension}</strong></td>
                    <td>{d.today}</td>
                    <td>{d.ewm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Everyone is adding memory. Nobody is adding an algebra for it.</h2>
          <p className="np-lead">
            We are not competing with retrieval or with long context — we are the layer underneath
            that gives either of them structure and guarantees.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '180px' }}>Approach</th>
                  <th>What is held</th>
                  <th>Guarantees</th>
                  <th>Coupling</th>
                  <th>Cost behavior</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((c) => (
                  <tr key={c.approach} className={c.highlight ? 'np-row-highlight' : undefined}>
                    <td><strong>{c.approach}</strong></td>
                    <td>{c.holds}</td>
                    <td>{c.guarantees}</td>
                    <td>{c.coupling}</td>
                    <td>{c.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">The state plane comes first; operations are organized around it</h2>
          <p className="np-lead">
            One workspace, twelve crates, no layer that depends on a host model. A new model is a
            new adapter — never a change to the foundation.
          </p>

          <div className="np-stats">
            {SCALE_FACTS.map((f) => (
              <div key={f.label} className="np-stat">
                <span className="np-stat-value">{f.value}</span>
                <span className="np-stat-label">{f.label}</span>
              </div>
            ))}
          </div>

          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '220px' }}>Layer</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {ARCHITECTURE_LAYERS.map((l) => (
                  <tr key={l.name}>
                    <td><strong>{l.name}</strong></td>
                    <td>{l.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="np-source" style={{ marginTop: '20px' }}>
            Design rule: the HLLSet state plane is prior. Applications are organized around the
            states, and host-specific knowledge lives only in the encoder adapter.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2 np-h2--center">Read the evidence, or bring us your model</h2>
          <p className="np-lead np-lead--center">
            The theory is written, the algebra is built, and the loop is proven on real models.
            What remains is productization.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/evidence" className="np-btn np-btn--primary">View measured results</Link>
            <Link to="/partners" className="np-btn np-btn--secondary">Design partner program</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Technology;
